<template>
  <div class="relative bg-black rounded-xl overflow-hidden h-full" ref="container">
    <video ref="video" class="w-full h-full object-contain" playsinline />
    <div v-if="!playerReady" class="absolute inset-0 flex items-center justify-center">
      <div class="text-center">
        <div class="loading-spinner mx-auto mb-2" />
        <p class="text-sm text-gray-400">Preparing editor...</p>
      </div>
    </div>
    <div v-if="seeking" class="absolute inset-0 flex items-center justify-center bg-black/40 z-20">
      <div class="text-center">
        <div class="loading-spinner mx-auto mb-2" />
        <p class="text-sm text-white/70">Loading...</p>
      </div>
    </div>
    <!-- Date overlay -->
    <div v-if="startDate && playerReady" class="absolute bottom-0 left-0 right-0 flex items-center justify-between px-4 py-2 bg-gradient-to-t from-black/60 to-transparent pointer-events-none">
      <span class="text-xs font-data text-white/70">
        <svg class="w-3 h-3 inline -mt-0.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        {{ formatDate(startDate) }}
      </span>
      <span v-if="endDate && mode !== 'live'" class="text-xs font-data text-white/70">
        {{ formatDate(endDate) }}
        <svg class="w-3 h-3 inline -mt-0.5 ml-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
      </span>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'

export default {
  name: 'EditorPlayer',
  emits: ['loop-seek', 'source-not-found'],

  computed: {
    ...mapState('editor', ['sourceUrl', 'mode', 'playing', 'playerReady', 'volume', 'muted',
      'loopMode', 'previewMode', 'previewEnd', 'markInTime', 'markOutTime', 'cuts', 'startDate', 'sourceData',
      'viewStart', 'viewDuration', 'isLive']),
    endDate() {
      const d = this.sourceData?.ended_at || this.sourceData?.transcoding?.channel?.ended_at
      return d ? new Date(d) : null
    }
  },

  data() {
    return { seeking: false }
  },

  watch: {
    sourceUrl() { this.loadVideo() },
    volume(v) { if (this.$refs.video) this.$refs.video.volume = v },
    muted(v) { if (this.$refs.video) this.$refs.video.muted = v }
  },

  mounted() {
    this._userDragging = false
    // Suppress AbortError from play() interrupted by load — harmless browser warning
    window.addEventListener('unhandledrejection', this._suppressAbortError = (e) => {
      if (e.reason?.name === 'AbortError') e.preventDefault()
    })
    if (this.sourceUrl) this.loadVideo()
    this.startAnimLoop()
  },

  beforeUnmount() {
    this.destroyHls()
    if (this._animId) cancelAnimationFrame(this._animId)
    if (this._suppressAbortError) window.removeEventListener('unhandledrejection', this._suppressAbortError)
  },

  methods: {
    ...mapMutations('editor', ['SET_PLAYING', 'SET_CURRENT_TIME', 'SET_DURATION', 'SET_PLAYER_READY', 'SET_IS_LIVE', 'SET_PREVIEW_MODE', 'SET_LOOP_MODE', 'SET_VIEW']),

    async loadVideo() {
      this.destroyHls()
      this._edgeProjection = null
      this._seenFragTagNames = null
      this.SET_PLAYER_READY(false)
      const video = this.$refs.video
      if (!video || !this.sourceUrl) return

      video.volume = this.volume
      video.muted = this.muted

      if (this.sourceUrl.endsWith('.m3u8') || this.mode === 'live' || this.mode === 'recording') {
        await this.loadHls(video, this.sourceUrl)
      } else {
        video.src = this.sourceUrl
        video.addEventListener('loadedmetadata', () => {
          this.SET_DURATION(video.duration)
          this.SET_PLAYER_READY(true)
        }, { once: true })
      }

      video.addEventListener('play', () => this.SET_PLAYING(true))
      video.addEventListener('pause', () => this.SET_PLAYING(false))
      video.addEventListener('ended', () => {
        // Keep loop modes running past 'ended' (fires at the last loaded segment).
        if (this.loopMode === 'all' && this.cuts.length > 0) {
          this.$emit('loop-seek', this.cuts[0].inTime)
          return
        }
        if (this.loopMode === 'selection' && this.markInTime !== null) {
          this.$emit('loop-seek', this.markInTime)
          return
        }
        this.SET_PLAYING(false)
      })
    },

    async loadHls(video, src) {
      try {
        const { default: Hls } = await import('hls.js')
        if (Hls.isSupported()) {
          this._hls = new Hls({
            // Editor mode: the user's manual seek is authoritative — they must
            // be able to scrub anywhere in the DVR window and stay put. hls.js
            // force-seeks the playhead to the live edge once it falls more than
            // `liveMaxLatencyDuration` behind; the old 2h cap meant seeking near
            // the start of a multi-hour DVR session yanked the user back to live
            // after ~1-2s. Keep it far beyond any DVR retention window so hls.js
            // never resyncs on its own (`liveSyncDuration` must stay below it).
            liveSyncDuration: 3600,
            liveMaxLatencyDuration: 30 * 24 * 3600,
            maxBufferLength: 30,
            maxMaxBufferLength: 60
          })
          this._hls.loadSource(src)
          this._hls.attachMedia(video)
          this._hls.on(Hls.Events.MANIFEST_PARSED, () => this._initFromManifest())
          // Fires after every playlist refresh (initial parse + live updates),
          // so a new SCTE-35 DATERANGE that appears mid-session gets picked up
          // without any extra polling. Deferred out of hls.js's synchronous
          // event dispatch with queueMicrotask so a slow scan can't starve
          // hls.js's own fetch / buffer scheduler and cause A/V slip.
          this._hls.on(Hls.Events.LEVEL_UPDATED, (_, data) => {
            const details = data?.details
            queueMicrotask(() => this._scanCommercialBreaks(details))
          })
          this._hls.on(Hls.Events.ERROR, (_, data) => {
            if (!data.fatal) return
            console.error('[Editor HLS Error]', data)
            // 404 on master or child manifest = no DVR/recording session for this source.
            const status = data.response?.code
            const isManifestLoad = data.details === 'manifestLoadError' ||
                                   data.details === 'manifestLoadTimeOut' ||
                                   data.details === 'levelLoadError' ||
                                   data.details === 'levelLoadTimeOut'
            if (isManifestLoad && status === 404) {
              this.$emit('source-not-found')
            }
          })
          // For finite VOD (recording with ENDLIST) the duration arrives via this event
          video.addEventListener('durationchange', () => {
            if (this.mode === 'live') return
            const dur = video.duration
            if (dur && isFinite(dur)) this.SET_DURATION(dur)
          })
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = src
          video.addEventListener('loadedmetadata', () => {
            this.SET_DURATION(video.duration)
            this.SET_PLAYER_READY(true)
          }, { once: true })
        }
      } catch {
        video.src = src
      }
    },

    // Anchor startDate to the first fragment's #EXT-X-PROGRAM-DATE-TIME so that
    // `video.currentTime` is, by construction, seconds since startDate. The export's
    // `startDate + currentTime*1000` then equals the real wall-clock with no offsets.
    _initFromManifest() {
      const video = this.$refs.video
      if (!video) return
      const tryInit = () => {
        const details = this._hls?.levels?.[this._hls.currentLevel]?.details
                      || this._hls?.levels?.[0]?.details
        const firstFrag = details?.fragments?.[0]
        if (!firstFrag?.programDateTime) {
          setTimeout(tryInit, 200)
          return
        }
        const firstPDT = new Date(firstFrag.programDateTime)
        this.$store.commit('editor/SET_START_DATE', firstPDT)
        this.$store.commit('editor/SET_HLS_TIME_OFFSET', 0)

        // Any LEVEL_UPDATED that fired before startDate was set bailed early.
        // Do a one-shot scan now so breaks already present in the manifest
        // show up without waiting for the next playlist refresh.
        this._scanCommercialBreaks(details)

        const liveEdge = this._computeLiveEdge()
        this.SET_DURATION(liveEdge)
        this.SET_IS_LIVE(this.mode === 'live')
        this.SET_PLAYER_READY(true)

        if (this.mode === 'live') {
          // Jump to the live edge and play
          if (liveEdge > 0) video.currentTime = Math.max(0, liveEdge - 0.5)
          const vd = 120
          this.SET_VIEW({ start: Math.max(0, liveEdge - vd * 0.98), duration: vd })
          video.play().catch(() => {})
        }
      }
      tryInit()
    },

    // Orchestrates every ad-break detector we support and forwards the
    // results into the editor store. Runs from LEVEL_UPDATED (every
    // playlist refresh) and once from _initFromManifest after startDate
    // gets anchored to the first fragment's PDT.
    _scanCommercialBreaks(details) {
      if (!details) return
      const startDate = this.$store.state.editor.startDate
      if (!startDate) return
      this._scanDateRangeBreaks(details, startDate.getTime())
      this._scanCueOutBreaks(details, startDate.getTime())
    },

    // Modern SCTE-35 DATERANGE — hls.js parses #EXT-X-DATERANGE into
    // `details.dateRanges` (a { [id]: DateRange } map) for us. Any range
    // carrying SCTE35-OUT / SCTE35-CMD (or Apple's DATERANGE-form
    // CUE-OUT variants) is treated as an ad break.
    _scanDateRangeBreaks(details, startMs) {
      if (!details.dateRanges) return

      const isAdBreak = (attr) => {
        if (!attr) return false
        return (
          attr['SCTE35-OUT'] != null ||
          attr['SCTE35-CMD'] != null ||
          attr['CUE-OUT'] != null ||
          attr['X-COM-APPLE-CUE-OUT'] != null
        )
      }

      const ranges = details.dateRanges instanceof Map
        ? Array.from(details.dateRanges.values())
        : Object.values(details.dateRanges)

      if (ranges.length) {
        // Overview line so you can see the scan is running and how many
        // DATERANGE tags the manifest carried. `attr` gets logged with the
        // full first range so you can eyeball what tags your encoder emits
        // if `matched` is 0 unexpectedly.
        const matched = ranges.filter(r => isAdBreak(r?.attr)).length
        console.log('[Editor] SCTE scan:',
          `${ranges.length} dateRange(s), ${matched} ad-break(s)`,
          ranges[0]?.attr || null
        )
      }

      for (const dr of ranges) {
        if (!dr || !dr.startDate) continue
        if (!isAdBreak(dr.attr)) continue

        const startTime = (dr.startDate.getTime() - startMs) / 1000
        // Prefer an explicit end date; fall back to duration / plannedDuration.
        // Some SCTE-35 OUT tags only carry duration and rely on a follow-up
        // SCTE35-IN range to define the true end — for display purposes we
        // just render whichever number arrives first.
        let endTime = null
        if (dr.endDate) {
          endTime = (dr.endDate.getTime() - startMs) / 1000
        } else if (typeof dr.duration === 'number' && dr.duration > 0) {
          endTime = startTime + dr.duration
        } else if (typeof dr.plannedDuration === 'number' && dr.plannedDuration > 0) {
          endTime = startTime + dr.plannedDuration
        }
        if (endTime == null || endTime <= startTime) endTime = startTime + 0.5

        this._commitCommercialBreak({
          id: dr.id || `dr-${dr.startDate.toISOString()}`,
          startTime,
          endTime,
          source: 'DATERANGE',
          raw: dr.attr
        })
      }
    },

    // Classic Apple / Elemental #EXT-X-CUE-OUT ... #EXT-X-CUE-IN pairs.
    // hls.js does NOT roll these into `dateRanges`, but it does attach the
    // top-level tags that immediately precede a fragment onto that
    // fragment's `tagList`. So we walk fragments in order and stitch each
    // CUE-OUT to the next CUE-IN into ONE break region.
    //
    // Tag shapes observed in the wild — we match all of them:
    //   #EXT-X-CUE-OUT:30                → ['EXT-X-CUE-OUT', '30']
    //   #EXT-X-CUE-OUT:DURATION=30,...   → ['EXT-X-CUE-OUT', 'DURATION=30,...']
    //   #EXT-X-CUE-OUT-CONT:15/30        → ['EXT-X-CUE-OUT-CONT', '15/30']
    //   #EXT-X-CUE-IN                    → ['EXT-X-CUE-IN']
    //
    // Why the id derivation matters: CUE-OUT-CONT appears on EVERY fragment
    // inside the break (once per segment). If each CONT tag minted its own
    // id, one 4s break with 6 fragments would render as 6 separate regions.
    // Instead we derive a stable id from the break's start PDT — subtracting
    // the CONT tag's `elapsed` value gets us back to that same PDT, so every
    // CONT inside the break collapses onto one id.
    _scanCueOutBreaks(details, startMs) {
      const frags = details.fragments
      if (!frags || !frags.length) return

      // Fragment-relative time in the same seconds-since-startDate domain
      // the rest of the editor uses. PDT wins; fall back to frag.start
      // (which is seconds-since-first-fragment, i.e. the same anchor).
      const fragTime = (frag) => {
        if (frag.programDateTime) return (frag.programDateTime - startMs) / 1000
        if (typeof frag.start === 'number') return frag.start
        return null
      }

      const isCueOut     = (name) => name === 'EXT-X-CUE-OUT' || name === 'CUE-OUT'
      const isCueOutCont = (name) => name === 'EXT-X-CUE-OUT-CONT' || name === 'CUE-OUT-CONT'
      const isCueIn      = (name) => name === 'EXT-X-CUE-IN'  || name === 'CUE-IN'

      // Stable id from the break-start PDT, rounded to whole seconds so a
      // CUE-OUT at PDT T and a later CUE-OUT-CONT with elapsed=E on a
      // fragment at PDT (T + E) collapse to the same id even when E is
      // fractional.
      const breakId = (breakStartPdtMs, fallbackSec) => {
        if (Number.isFinite(breakStartPdtMs)) {
          return `cue-${Math.round(breakStartPdtMs / 1000)}`
        }
        return `cue-t-${Math.round(fallbackSec)}`
      }

      // Parse a CUE-OUT tag value into a planned duration in seconds.
      //   "30"                     → 30
      //   "DURATION=30,SCTE35=..." → 30
      //   "15/30" (CUE-OUT-CONT)   → 30  (planned total, not elapsed)
      const parseDuration = (raw) => {
        if (!raw) return 0
        const s = String(raw).trim()
        const durMatch = s.match(/(?:^|,)\s*DURATION\s*=\s*([\d.]+)/i)
        if (durMatch) return Number(durMatch[1])
        const contMatch = s.match(/^([\d.]+)\s*\/\s*([\d.]+)/)
        if (contMatch) return Number(contMatch[2])
        if (/^[\d.]+$/.test(s)) return Number(s)
        return 0
      }

      // Parse elapsed/total out of a CUE-OUT-CONT value ("15/30" → 15).
      const parseElapsed = (raw) => {
        if (!raw) return 0
        const m = String(raw).trim().match(/^([\d.]+)\s*\/\s*[\d.]+/)
        return m ? Number(m[1]) : 0
      }

      // Log every distinct tag name hls.js surfaces on fragment tagLists —
      // only when a NEW name shows up so live-stream LEVEL_UPDATEDs (every
      // few seconds) don't spam the console. This is the fast way to
      // confirm CUE-OUT is actually landing in `tagList`; if you never see
      // it here, hls.js stripped it and we'd need to hit the raw playlist
      // text instead.
      if (!this._seenFragTagNames) this._seenFragTagNames = new Set()
      const newlySeen = []
      for (const frag of frags) {
        if (!frag.tagList) continue
        for (const t of frag.tagList) {
          if (Array.isArray(t) && t.length && !this._seenFragTagNames.has(t[0])) {
            this._seenFragTagNames.add(t[0])
            newlySeen.push(t[0])
          }
        }
      }
      if (newlySeen.length) {
        console.log('[Editor] New fragment tag names:', newlySeen)
      }

      // Merge algorithm: walk fragments IN ORDER and treat any run of
      // consecutive fragments that carry a CUE-OUT / CUE-OUT-CONT as ONE
      // break. This is the right model regardless of whether the encoder
      // emits CUE-OUT once (at the break start) or on every fragment inside
      // the break — either way the region we render should span from the
      // first tagged fragment's start to the last tagged fragment's end.
      // A break ends when we see CUE-IN OR when the next fragment has no
      // cue tags at all (natural gap).
      const collected = new Map() // id -> { id, startTime, endTime }
      let openBreak = null        // { id, startTime, endTime }
      let cueTagCount = 0

      const closeOpen = () => {
        if (!openBreak) return
        collected.set(openBreak.id, openBreak)
        openBreak = null
      }

      for (const frag of frags) {
        const tagList = frag.tagList
        let hasOut = false
        let hasIn = false
        let outValue = null
        let contValue = null

        if (tagList && tagList.length) {
          for (const tag of tagList) {
            if (!Array.isArray(tag) || !tag.length) continue
            const name = tag[0]
            if (isCueOut(name))     { hasOut = true; outValue  = tag[1]; cueTagCount++ }
            else if (isCueOutCont(name)) { hasOut = true; contValue = tag[1]; cueTagCount++ }
            else if (isCueIn(name)) { hasIn  = true;                     cueTagCount++ }
          }
        }

        const t = fragTime(frag)
        if (t == null) continue
        const fragEnd = t + (frag.duration || 0)

        if (hasOut) {
          if (!openBreak) {
            // First CUE tag of a new run — anchor break start. If it's a
            // CONT we walk back by `elapsed` to reach the true start (we
            // joined mid-break); otherwise the fragment's own start IS
            // the break start.
            let breakStart = t
            let breakStartPdt = frag.programDateTime
            if (contValue) {
              const elapsed = parseElapsed(contValue)
              if (elapsed > 0) {
                breakStart = t - elapsed
                breakStartPdt = frag.programDateTime ? frag.programDateTime - elapsed * 1000 : NaN
              }
            }
            openBreak = {
              id: breakId(breakStartPdt, breakStart),
              startTime: breakStart,
              endTime: fragEnd
            }
            // Take a planned duration hint if the tag carries one. Extends
            // the region past the currently-seen fragments so a live break
            // still renders its full expected width before the CUE-IN
            // fragment arrives in the manifest.
            const planned = parseDuration(outValue) || parseDuration(contValue)
            if (planned > 0) {
              openBreak.endTime = Math.max(openBreak.endTime, breakStart + planned)
            }
          } else {
            // Continuation of the current run — just extend the right edge.
            openBreak.endTime = Math.max(openBreak.endTime, fragEnd)
            const planned = parseDuration(outValue) || parseDuration(contValue)
            if (planned > 0) {
              openBreak.endTime = Math.max(openBreak.endTime, openBreak.startTime + planned)
            }
          }
        }

        if (hasIn && openBreak) {
          // Authoritative close: CUE-IN clamps end to this fragment's start.
          openBreak.endTime = t
          closeOpen()
        } else if (!hasOut && !hasIn && openBreak) {
          // Non-CUE fragment ends the run — natural break termination.
          closeOpen()
        }
      }
      // End of fragment list with a still-open break (typical for live
      // where the CUE-IN hasn't been appended yet).
      if (openBreak) closeOpen()

      // Commit the merged set. The idempotence guard inside
      // _commitCommercialBreak means unchanged breaks skip the store.
      for (const brk of collected.values()) {
        this._commitCommercialBreak({
          id: brk.id,
          startTime: brk.startTime,
          endTime: brk.endTime,
          source: 'CUE-OUT/IN',
          raw: null
        })
      }

      if (cueTagCount) {
        console.log('[Editor] CUE-OUT/IN scan:',
          `${cueTagCount} cue tag(s) across ${frags.length} fragment(s)`,
          `→ ${collected.size} merged break(s)`
        )
      }
    },

    // Common commit path: dedupe against the store, log on first sighting,
    // upsert. Every source (DATERANGE, CUE-OUT, future ones) funnels
    // through here so the logging shape stays consistent.
    //
    // Idempotence matters: LEVEL_UPDATED fires every ~6s for live, and each
    // scan re-processes every break already in the manifest. Without this
    // early-return the store gets a fresh mutation for every existing break
    // on every refresh — that walks the array, triggers Vue reactivity,
    // and churns the timeline enough to visibly slip A/V sync.
    _commitCommercialBreak({ id, startTime, endTime, source, raw }) {
      if (endTime == null || endTime <= startTime) endTime = startTime + 0.5
      const known = this.$store.state.editor.commercialBreaks
      const existing = known.find(b => b.id === id)

      // 1s tolerance: repeat scans on live streams can drift by fractions
      // of a second due to hls.js's playlist re-parse (level switch, PDT
      // re-alignment, etc.) even when the underlying break didn't change.
      // Tighter than 1s meant every LEVEL_UPDATED fired an "end updated"
      // for every break, which was pure noise. Real end shifts from a
      // CUE-IN arriving on a later scan will be seconds, not sub-second.
      const IDEMPOTENCE_TOLERANCE = 1.0
      if (existing
          && Math.abs(existing.startTime - startTime) < IDEMPOTENCE_TOLERANCE
          && Math.abs(existing.endTime - endTime) < IDEMPOTENCE_TOLERANCE) {
        return
      }

      const video = this.$refs.video
      const currentTime = video ? video.currentTime : null
      const delta = currentTime != null ? currentTime - startTime : null

      if (!existing) {
        // Timing diagnostic: `videoCurrentTime` is where playback is RIGHT
        // NOW; `startTime` is where the break begins on the timeline; `delta`
        // is currentTime - startTime.
        //   delta < 0 → region is drawn ahead of the playhead (expected —
        //     hls.js parsed the CUE tag before playback reached it).
        //   delta ≈ 0 → tag arrived just as the ad started (tight, but ok).
        //   delta > 0 → tag arrived AFTER the ad already began playing.
        //     Either the manifest refresh that added it happened late, or
        //     the fragment's PDT drifted relative to video.currentTime
        //     (common with SSAI stitchers that give ad segments their own
        //     PDT). Look at delta on a few breaks — if it's consistently
        //     the same positive number, that's the encoder's insertion lag.
        console.log('[Editor] Commercial break detected:', {
          id,
          source,
          startTime: startTime.toFixed(2) + 's',
          endTime: endTime.toFixed(2) + 's',
          duration: (endTime - startTime).toFixed(2) + 's',
          videoCurrentTime: currentTime != null ? currentTime.toFixed(2) + 's' : 'n/a',
          delta: delta != null ? (delta >= 0 ? '+' : '') + delta.toFixed(2) + 's' : 'n/a',
          raw
        })
      } else {
        // Real change (>= 1s in either edge). Log both sides so we can see
        // whether it was the start or the end that moved, and by how much.
        console.log('[Editor] Commercial break end updated:', {
          id,
          source,
          oldStart: existing.startTime.toFixed(2) + 's',
          newStart: startTime.toFixed(2) + 's',
          oldEnd: existing.endTime.toFixed(2) + 's',
          newEnd: endTime.toFixed(2) + 's',
          startDelta: (startTime - existing.startTime).toFixed(2) + 's',
          endDelta: (endTime - existing.endTime).toFixed(2) + 's',
          newDuration: (endTime - startTime).toFixed(2) + 's',
          videoCurrentTime: currentTime != null ? currentTime.toFixed(2) + 's' : 'n/a'
        })
      }

      this.$store.commit('editor/UPSERT_COMMERCIAL_BREAK', {
        id,
        startTime,
        endTime,
        duration: endTime - startTime
      })
    },

    // Seekable end (covers EVENT-type live where video.duration is Infinity).
    _computeLiveEdge() {
      const video = this.$refs.video
      if (!video) return 0
      if (video.seekable && video.seekable.length > 0) {
        return video.seekable.end(video.seekable.length - 1)
      }
      if (video.duration && isFinite(video.duration)) return video.duration
      const details = this._hls?.levels?.[this._hls.currentLevel]?.details
                    || this._hls?.levels?.[0]?.details
      if (details?.fragments?.length) {
        const last = details.fragments[details.fragments.length - 1]
        return (last.start || 0) + (last.duration || 0)
      }
      return 0
    },

    destroyHls() {
      const video = this.$refs.video
      if (video) video.pause()
      if (this._hls) {
        this._hls.detachMedia()
        this._hls.destroy()
        this._hls = null
      }
      // Clear stale buffered state on the video element so the next attachMedia
      // doesn't race with leftover MediaSource buffers from the previous source.
      if (video) {
        try {
          video.removeAttribute('src')
          video.load()
        } catch (e) { /* noop */ }
      }
    },

    startAnimLoop() {
      const tick = () => {
        const video = this.$refs.video
        if (video) {
          const ct = video.currentTime
          this.SET_CURRENT_TIME(ct)

          if (this.mode === 'live' && this.playing) {
            const playableEdge = this._computeLiveEdge()
            if (playableEdge > 0) {
              // Keep a smooth projection of the live edge warm. `seekable.end()`
              // only advances when hls.js appends a fragment (~every 6s); the
              // projection advances every frame and eases toward it, so the
              // content bar's right edge stays smooth while scrubbed back.
              const now = performance.now()
              const dt = Math.min(0.1, (now - (this._edgeTs || now)) / 1000)
              this._edgeTs = now
              if (this._edgeProjection == null || Math.abs(playableEdge - this._edgeProjection) > 30) {
                // First frame, or a real discontinuity (resync / level switch) — snap.
                this._edgeProjection = playableEdge
              } else {
                this._edgeProjection += dt + (playableEdge - this._edgeProjection) * dt * 0.2
              }

              // "Near the live edge" check uses actual playable content — the
              // playable edge lags wall-clock by encoder/upload latency.
              if (!this.$store.state.editor.isLive && playableEdge - ct < 5) {
                this.SET_IS_LIVE(true)
              }

              // At the live edge the playhead IS the newest content, so the
              // content bar must end exactly there — drive duration from the
              // smooth media clock. Feeding the edge projection here instead
              // made the bar's right edge breathe in and out relative to the
              // pinned playhead (the projection and `currentTime` advance at
              // slightly different rates). Scrubbed back, duration tracks the
              // projected edge so the bar still shows everything available.
              this.SET_DURATION(this.$store.state.editor.isLive ? ct : this._edgeProjection)
            }
          } else if (this.playing) {
            this.ensurePlayheadVisible(ct)
          }

          // Loop selection
          if (this.playing && this.loopMode === 'selection' && this.markInTime !== null && this.markOutTime !== null) {
            if (ct >= this.markOutTime) video.currentTime = this.markInTime
          } else if (this.playing && this.loopMode === 'all' && this.cuts.length > 0) {
            let jumpTo = null
            for (let i = 0; i < this.cuts.length; i++) {
              const cut = this.cuts[i]
              if (ct >= cut.outTime) {
                const nextCut = this.cuts[i + 1]
                if (nextCut && ct < nextCut.inTime) { jumpTo = nextCut.inTime; break }
                else if (!nextCut) { jumpTo = this.cuts[0].inTime; break }
              } else if (ct < cut.inTime && i === 0) {
                jumpTo = cut.inTime
                break
              }
            }
            if (jumpTo !== null) video.currentTime = jumpTo
          }

          // Preview end
          if (this.playing && this.previewEnd !== null && ct >= this.previewEnd) {
            video.pause()
            this.SET_PREVIEW_MODE({ mode: null, end: null })
          }
        }
        this._animId = requestAnimationFrame(tick)
      }
      this._animId = requestAnimationFrame(tick)
    },

    ensurePlayheadVisible(ct) {
      if (this._userDragging) return
      const vs = this.$store.state.editor.viewStart
      const vd = this.$store.state.editor.viewDuration
      const rightThreshold = vs + vd * 0.9
      const leftThreshold = vs + vd * 0.1
      if (ct > rightThreshold) {
        this.SET_VIEW({ start: Math.max(0, ct - vd * 0.9) })
      } else if (ct < leftThreshold && ct < vs) {
        this.SET_VIEW({ start: Math.max(0, ct - vd * 0.1) })
      }
    },

    setUserDragging(val) { this._userDragging = val },

    // Real wall-clock Date of the current playhead from hls.js fragment PDT.
    // Kept for diagnostics / future cut-accuracy checks. Returns null if unavailable.
    getPlayheadProgramDate() {
      const video = this.$refs.video
      if (!video || !this._hls) return null
      const details = this._hls.levels?.[this._hls.currentLevel]?.details
        || this._hls.levels?.[0]?.details
      const frags = details?.fragments
      if (!frags || !frags.length) return null
      const t = video.currentTime
      for (const f of frags) {
        if (f.programDateTime == null) continue
        if (t >= f.start && t < f.start + f.duration) {
          return new Date(f.programDateTime + (t - f.start) * 1000)
        }
      }
      for (let i = frags.length - 1; i >= 0; i--) {
        const f = frags[i]
        if (f.programDateTime != null) {
          return new Date(f.programDateTime + (t - f.start) * 1000)
        }
      }
      return null
    },

    formatDate(d) {
      if (!d) return ''
      const date = new Date(d)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const year = date.getFullYear()
      const h = String(date.getHours()).padStart(2, '0')
      const m = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${month}/${day}/${year} ${h}:${m}:${s}`
    },

    // Public methods called by parent
    play() { this.$refs.video?.play() },
    pause() { this.$refs.video?.pause() },
    togglePlay() { this.playing ? this.pause() : this.play() },

    seek(time) {
      const video = this.$refs.video
      if (!video) return
      // Clicks past the playable live edge (in the wall-clock-vs-segment-upload
      // lag zone) have no content to seek to — treat them like Go Live.
      if (this.mode === 'live') {
        const playableEdge = this._computeLiveEdge()
        if (playableEdge > 0 && time > playableEdge) {
          this.goLive()
          return
        }
      }
      video.currentTime = time
      if (this.mode === 'live') this.SET_IS_LIVE(false)
    },

    goLive() {
      const video = this.$refs.video
      if (!video) return
      const liveEdge = this._computeLiveEdge()
      if (liveEdge > 0) video.currentTime = Math.max(0, liveEdge - 0.5)
      this.SET_IS_LIVE(true)
      const vd = this.$store.state.editor.viewDuration
      this.SET_VIEW({ start: Math.max(0, video.currentTime - vd * 0.98) })
      if (!this.playing) video.play().catch(() => {})
    }
  }
}
</script>

<style scoped>
.loading-spinner {
  width: 28px; height: 28px;
  border: 3px solid #374151; border-top-color: var(--color-primary);
  border-radius: 50%; animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
